<script lang="ts" setup>
import type { Ref } from "vue";
import type {
    PinturaDefaultImageWriterResult,
    PinturaEditor as PinturaEditorObject,
    PinturaEditorModal as PinturaEditorModalObject,
} from "@pqina/pintura";
import { faEdit } from "@fortawesome/pro-regular-svg-icons";

type PinturaEditorObjectType = PinturaEditorObject & PinturaEditorModalObject;

const props = withDefaults(
    defineProps<{
        id?: string;
        name?: string;
        round?: boolean;
        type?: "auto" | "avatar";
        class?: any;
        size?:
            | "small"
            | "medium"
            | "large"
            | string
            | number
            | { width: number | string; height: number | string };
        displaySize?: string | { width: string; height: string };
        src: string | File | undefined;
        shrinkIcon?: number;
        iconHeight?: number;
        pinturaAspectRatio?: number;
        imageClass?: string;
    }>(),
    {
        imageClass: "",
        round: false,
        type: "auto",
        shrinkIcon: 0,
    },
);

const emit = defineEmits<{
    (e: "update:src", src: string | File): void;
}>();

const input = ref<HTMLInputElement>();
const image = ref<string | undefined>();
const instance = ref();
const dropZoneRef = ref<HTMLDivElement>();
const { isOverDropZone } = useDropZone(dropZoneRef, (files) => {
    if (files && files.length) {
        for (const file of files)
            if (file.type.startsWith("image/")) {
                onUpload({
                    target: {
                        files: [file],
                    },
                } as unknown as Event);
                break;
            }
    }
});

const actualSize = computed(() => {
    switch (props.size) {
        case "small":
            return 28;
        case "large":
            return 40;
        case "medium":
            return 34;
        default:
            return props.size;
    }
});

const height = computed(() => {
    return calculateSize("height");
});

const width = computed(() => {
    return calculateSize("width");
});

const iconSize = computed(() => {
    if (props.iconHeight) return `${props.iconHeight}px`;

    let size = height.value;

    size = size === "auto" ? 0 : parseInt(size) - props.shrinkIcon;

    return size ? `${Math.round(size * 0.8)}px` : "30px";
});

const size = computed(() => {
    let w = parseInt(width.value);
    let h = parseInt(height.value);

    if (!h || !w) return undefined;
    return { width: w, height: h };
});

const aspectRatio = ref();

watchImmediate([() => props.pinturaAspectRatio, () => props.size], () => {
    if (props.pinturaAspectRatio) {
        aspectRatio.value = props.pinturaAspectRatio;
        return;
    }

    aspectRatio.value = undefined;
});

const displayImage = computed(() => {
    return typeof props.src === "string" || !props.src
        ? props.src
        : URL.createObjectURL(props.src);
});

function calculateSize(key: string) {
    switch (typeof actualSize.value) {
        case "number":
            return `${actualSize.value}px`;
        case "string":
            return actualSize.value;
        case "object":
            let size = actualSize.value[key] ?? "auto";
            return typeof size === "number" ? `${size}px` : size;
        default:
            return "auto";
    }
}

function onClick() {
    input.value.click();
}

function onUpload(event: Event) {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
        image.value = URL.createObjectURL(target.files[0]);
        instance.value.show();
    }
}

function onInit(context: { instance: Ref<PinturaEditorObjectType> }) {
    if (instance.value === undefined) {
        instance.value = context.instance.value;
        watch(context.instance, (to) => (instance.value = to));
    }
}

const lastClosed = ref(Date.now());

function onClose() {
    image.value = undefined;

    lastClosed.value = Date.now();

    if (input.value) input.value.value = "";
}

function onFinish({
    event,
}: {
    event: CustomEvent<PinturaDefaultImageWriterResult>;
}) {
    emit("update:src", event.detail.dest);
}

const display = computed(() => ({
    height: props.displaySize
        ? (props.displaySize.height ?? props.displaySize)
        : height.value,
    width: props.displaySize
        ? (props.displaySize.width ?? props.displaySize)
        : width.value,
    minWidth: props.displaySize
        ? (props.displaySize.height ?? props.displaySize)
        : height.value,
}));

const cropSelectPresetFilter = ref("landscape");

const cropSelectPresetOptions = ref([
    [undefined, "Custom"],
    [1, "Square"],

    // shown when cropSelectPresetFilter is set to 'landscape'
    [2 / 1, "2:1"],
    [3 / 2, "3:2"],
    [4 / 3, "4:3"],
    [16 / 10, "16:10"],
    [16 / 9, "16:9"],

    // shown when cropSelectPresetFilter is set to 'portrait'
    [1 / 2, "1:2"],
    [2 / 3, "2:3"],
    [3 / 4, "3:4"],
    [10 / 16, "10:16"],
    [9 / 16, "9:16"],
]);

function trigger() {
    input.value.click();
}
</script>

<template>
    <div
        ref="dropZoneRef"
        :class="[
            {
                'rounded-full': round,
                'rounded-md': type === 'avatar' && !round,
                'scale-[1.05]': isOverDropZone,
            },
            props.class,
        ]"
        :style="display"
        class="transform transition bg-hd-neutral-300 dark:bg-hd-neutral-700 duration-150 inline-flex items-center justify-center relative overflow-hidden"
        @click="onClick"
    >
        <div
            :class="isOverDropZone || !src ? 'opacity-100' : 'opacity-0'"
            class="cursor-pointer hover:opacity-100 transition duration-150 z-10 absolute w-full h-full bg-gray-700/30 flex items-center justify-center"
        >
            <FontAwesomeIcon
                :icon="faEdit"
                :style="{ width: iconSize, height: iconSize }"
                class="text-white"
            />
        </div>
        <UIAvatar
            v-if="type === 'avatar'"
            :class="[{ 'rounded-full': round }, imageClass]"
            :src="displayImage"
            :style="{ height: height }"
            classs="w-auto"
            size="none"
        />
        <NuxtImg
            v-else-if="displayImage"
            :class="imageClass"
            :src="displayImage"
            :style="display"
            class="rounded-lg"
        />
        <HdPintura
            :key="lastClosed"
            v-model:src="image"
            :cropSelectPresetFilter="cropSelectPresetFilter"
            :cropSelectPresetOptions="cropSelectPresetOptions"
            :imageCropAspectRatio="aspectRatio"
            :imageCropMinSize="size"
            type="modal"
            v-on:pintura:init="onInit"
            v-on:pintura:close="onClose"
            v-on:pintura:hide="onClose"
            v-on:pintura:process="onFinish"
        />
        <input
            :id="id"
            ref="input"
            :name="id"
            accept="image/*"
            class="hidden"
            type="file"
            v-on:input="onUpload"
        />
    </div>
    <slot :trigger="trigger" />
</template>
